/**
 * Stage Content Component
 * Router component that renders different UI for each workflow stage
 */

import React from 'react';
import type { CourseDraft } from '../../types/creator-workflow';
import Stage0MaterialInput from './stages/Stage0MaterialInput';
import Stage1Generation from './stages/Stage1Generation';
import SentenceEditingStage from './stages/SentenceEditingStage';
import Stage2Parsing from './stages/Stage2Parsing';
import Stage3AudioGeneration from './stages/Stage3AudioGeneration';
import Stage4Validation from './stages/Stage4Validation';
import Stage5Publishing from './stages/Stage5Publishing';
import StageConversationalEditing from './stages/StageConversationalEditing';
import './StageContent.css';


interface StageContentProps {
  draft: CourseDraft | null;
  loading: boolean;
  onCreateDraft: (data: any) => Promise<CourseDraft | null>;
  onUpdateDraft: (data: any) => Promise<void>;
  onGenerateContent: (data: any, draftId?: string) => Promise<void>;
  onStartSentenceEditing: () => Promise<void>;
  onParseContent: () => Promise<void>;
  onGenerateAudio: (options?: any) => Promise<void>;
  onGenerateSegmentedAudio: (options?: any) => Promise<void>;
  onValidate: () => Promise<any>;
  onAdvanceStage: () => Promise<void>;
  onGoBackStage: () => Promise<void>;
  onRefreshDraft?: () => Promise<void>;
  onSetGlobalProcessing?: (isProcessing: boolean, message?: string) => void;
  onDraftUpdate?: (draft: CourseDraft) => void;
  // Sentence editing CRUD handlers
  onUpdateSentenceEditing?: (index: number, data: { text: string; speaker?: string | null }) => Promise<void>;
  onSplitSentenceEditing?: (index: number, data: { firstText: string; secondText: string; secondSpeaker?: string | null }) => Promise<void>;
  onMergeNextSentenceEditing?: (index: number) => Promise<void>;
  onDeleteSentenceEditing?: (index: number) => Promise<void>;
  onAddSentenceEditing?: (data: { afterIndex?: number | null; text: string; speaker?: string | null }) => Promise<void>;
}

const StageContent: React.FC<StageContentProps> = ({
  draft,
  loading,
  onCreateDraft,
  onUpdateDraft,
  onGenerateContent,
  onStartSentenceEditing,
  onParseContent,
  onGenerateAudio,
  onGenerateSegmentedAudio,
  onValidate,
  onAdvanceStage,
  onGoBackStage,
  onRefreshDraft,
  onSetGlobalProcessing,
  onDraftUpdate,
  onUpdateSentenceEditing,
  onSplitSentenceEditing,
  onMergeNextSentenceEditing,
  onDeleteSentenceEditing,
  onAddSentenceEditing,
}) => {
  // If no draft, show Stage 0 (Material Input) to create a new draft
  if (!draft) {
    console.log('📋 StageContent: No draft, showing Stage0MaterialInput');
    return (
      <Stage0MaterialInput
        draft={null}
        onCreateDraft={onCreateDraft}
        onUpdateDraft={onUpdateDraft}
        onAdvanceStage={onAdvanceStage}
        loading={loading}
        onRefreshDraft={onRefreshDraft}
        onSetGlobalProcessing={onSetGlobalProcessing}
      />
    );
  }

  // Route to appropriate stage component based on current_stage
  // Support both uppercase and lowercase stage names
  const stage = draft.current_stage.toUpperCase();
  console.log('📋 StageContent: Rendering for stage:', stage, '(raw:', draft.current_stage, ')');

  switch (stage) {
    case 'MATERIAL_INPUT':
      return (
        <Stage0MaterialInput
          draft={draft}
          onCreateDraft={onCreateDraft}
          onUpdateDraft={onUpdateDraft}
          onAdvanceStage={onAdvanceStage}
          loading={loading}
          onRefreshDraft={onRefreshDraft}
          onSetGlobalProcessing={onSetGlobalProcessing}
        />
      );

    case 'CONVERSATIONAL_EDITING':
      return (
        <StageConversationalEditing
          draft={draft}
          onAdvanceStage={onAdvanceStage}
          onGoBackStage={onGoBackStage}
          onDraftUpdate={onDraftUpdate}
          onParseContent={onParseContent}
          loading={loading}
        />
      );

    case 'GENERATION':
      return (
        <Stage1Generation
          draft={draft}
          onCreateDraft={onCreateDraft}
          onGenerateContent={onGenerateContent}
          onStartSentenceEditing={onStartSentenceEditing}
          onParseContent={onParseContent}
          onAdvanceStage={onAdvanceStage}
          loading={loading}
        />
      );

    case 'SENTENCE_EDITING':
      return (
        <SentenceEditingStage
          draft={draft}
          loading={loading}
          onUpdateSentenceEditing={onUpdateSentenceEditing || (async () => { /* noop */ })}
          onSplitSentenceEditing={onSplitSentenceEditing || (async () => { /* noop */ })}
          onMergeNextSentenceEditing={onMergeNextSentenceEditing || (async () => { /* noop */ })}
          onDeleteSentenceEditing={onDeleteSentenceEditing || (async () => { /* noop */ })}
          onAddSentenceEditing={onAddSentenceEditing || (async () => { /* noop */ })}
          onConfirmAndParse={onParseContent}
          onGoBackStage={onGoBackStage}
        />
      );

    case 'PARSING':
      return (
        <Stage2Parsing
          draft={draft}
          onParseContent={onParseContent}
          onAdvanceStage={onAdvanceStage}
          onGoBackStage={onGoBackStage}
          onRefreshDraft={onRefreshDraft}
          onUpdateDraft={onUpdateDraft}
          loading={loading}
        />
      );

    case 'AUDIO_GENERATION':
      return (
        <Stage3AudioGeneration
          draft={draft}
          onGenerateAudio={onGenerateAudio}
          onGenerateSegmentedAudio={onGenerateSegmentedAudio}
          onAdvanceStage={onAdvanceStage}
          onGoBackStage={onGoBackStage}
          loading={loading}
        />
      );

    case 'VALIDATION':
      return (
        <Stage4Validation
          draft={draft}
          onValidate={onValidate}
          onAdvanceStage={onAdvanceStage}
          onDraftUpdate={onDraftUpdate}
          loading={loading}
        />
      );

    case 'PUBLISHING':
      return (
        <Stage5Publishing
          draft={draft}
          loading={loading}
          onGoBackStage={onGoBackStage}
        />
      );

    default:
      // Log error for debugging but don't show error UI to user
      // Fallback to Generation stage for unknown stages
      console.warn(`Unknown stage: ${draft.current_stage} (normalized: ${stage}), falling back to GENERATION`);
      return (
        <Stage1Generation
          draft={draft}
          onCreateDraft={onCreateDraft}
          onGenerateContent={onGenerateContent}
          onStartSentenceEditing={onStartSentenceEditing}
          onParseContent={onParseContent}
          onAdvanceStage={onAdvanceStage}
          loading={loading}
        />
      );
  }
};

export default StageContent;
